import React, {useState} from 'react';
import {Icon} from '@iconify/react';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useDebounce} from 'use-debounce';
import Button from '../../../../../../incase-domain/components/button/Button';
import {RequestParam} from '../../../../../../incase-domain/requestParams';
import {isSlugConflictError, PresentationSlugParam, useCheckSlugAvailable, useCreateSlug} from '../../../../../../service/hooks/useSlug';
import ParamPresets from './ParamPresets';
import {hasInvalidRedirectPreset} from '../../../../../../utils/redirect';
import {SLUG_MAX, validateSlugFormat} from './slugValidation';
import {QUERY_KEYS} from '../../../../../../utils/queryKeys';

interface AddSlugFormProps {
    presentationId: string;
    requestParams: RequestParam[];
    onCreated: () => void;
}

function AddSlugForm({presentationId, requestParams, onCreated}: AddSlugFormProps): JSX.Element {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [slug, setSlug] = useState('');
    const [debouncedSlug] = useDebounce(slug, 400);
    const [params, setParams] = useState<PresentationSlugParam[]>([]);
    const [formatError, setFormatError] = useState<string | null>(null);

    const {mutate: create, isLoading: isCreating} = useCreateSlug();

    const shouldCheck = open && !!slug && !formatError && slug === debouncedSlug;
    const {data: available, isFetching: checking} = useCheckSlugAvailable(shouldCheck ? debouncedSlug : '');

    const handleCreate = (): void => {
        if (formatError || available !== true || slug !== debouncedSlug) return;
        create(
            {presentationId, slug, params},
            {
                onSuccess: () => {
                    toast.success(t('toasts.successfullySaved'));
                    setOpen(false);
                    setSlug('');
                    setParams([]);
                    setFormatError(null);
                    queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.byPresentation(presentationId));
                    queryClient.invalidateQueries(QUERY_KEYS.presentationParam.byPresentation(presentationId));
                    onCreated();
                },
                onError: (error) => {
                    toast.error(t(isSlugConflictError(error) ? 'presEditPage.settings.shortLink.taken' : 'toasts.errorOccurred'));
                },
            }
        );
    };

    if (!open) {
        return (
            <Button type="button" variant="ghost" color="info" size="sm" onClick={() => setOpen(true)} leading="solar:add-circle-linear">
                {t('presEditPage.settings.shortLink.addSlug')}
            </Button>
        );
    }

    const isSaveDisabled =
        !slug || !!formatError || available !== true || slug !== debouncedSlug || hasInvalidRedirectPreset(params) || isCreating;

    return (
        <div className="c-settingsItem c-slugSettings__item">
            <div className="c-slugSettings__row">
                <div className="c-slugSettings__inputWrap">
                    <div className="c-sidepanel__input">
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => {
                                const val = e.target.value.toLowerCase();
                                setSlug(val);
                                setFormatError(validateSlugFormat(val));
                            }}
                            placeholder={t('presEditPage.settings.shortLink.slugPlaceholder')}
                            maxLength={SLUG_MAX}
                        />
                    </div>
                    {checking && (
                        <span className="c-slugSettings__statusIcon">
                            <Icon icon="solar:refresh-linear" width={14} />
                        </span>
                    )}
                    {!checking && slug && !formatError && available === true && (
                        <span className="c-slugSettings__statusIcon c-slugSettings__statusIcon--success">
                            <Icon icon="solar:check-circle-linear" width={14} />
                        </span>
                    )}
                    {!checking && slug && (available === false || !!formatError) && (
                        <span className="c-slugSettings__statusIcon c-slugSettings__statusIcon--error">
                            <Icon icon="solar:close-circle-linear" width={14} />
                        </span>
                    )}
                </div>
                <Button
                    type="button"
                    variant="solid"
                    color="info"
                    size="sm"
                    onClick={handleCreate}
                    disabled={isSaveDisabled}
                    loading={isCreating}
                >
                    {t('common.save')}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        setOpen(false);
                        setSlug('');
                        setParams([]);
                        setFormatError(null);
                    }}
                >
                    {t('common.cancel')}
                </Button>
            </div>

            {formatError && <span className="is-error c-slugSettings__hint--error">{formatError}</span>}
            {!formatError && slug && available === false && (
                <span className="is-error c-slugSettings__hint--error">{t('presEditPage.settings.shortLink.taken')}</span>
            )}
            {!formatError && slug && available === true && (
                <span className="c-slugSettings__hint--success">{t('presEditPage.settings.shortLink.available')}</span>
            )}
            {!slug && <span className="c-slugSettings__hint">{t('presEditPage.settings.shortLink.slugHint')}</span>}

            {requestParams?.length > 0 && (
                <div className="c-slugSettings__params">
                    <p className="c-slugSettings__paramsTitle">{t('presEditPage.settings.shortLink.params')}</p>
                    <ParamPresets requestParams={requestParams} value={params} onChange={setParams} />
                </div>
            )}
        </div>
    );
}

export default AddSlugForm;
